import { HTTP } from '@cerbos/http';
import { v4 } from 'uuid';

import { resolveCerbosUrl } from '../../core/endpoints';
import { isDefined } from '../../utils/variables';
import { TokenService } from '../authentication/token.service';
import { CerbosFeature } from './features';
import { CerbosView } from './views';

export type CerbosResourceKind = 'view' | 'feature';

export type CerbosProjectRoles = 'user' | 'admin';

export type CerbosViewScope = 'skymap' | 'moba' | 'zynka';

export type CerbosScope = CerbosViewScope;

export interface CerbosPrincipalAttributes {
  projects?: { id: string; role: CerbosProjectRoles; hasDesignSeat?: boolean }[];
  companyUsers?: string[];
  isSharedView?: boolean;
}
export interface CerbosPrincipal {
  id?: string;
  attributes?: CerbosPrincipalAttributes;
}
export interface CerbosResource<T extends CerbosResourceKind> {
  id?: string;
  kind: T;
  scope?: CerbosScope;
  attributes: {
    projectId?: string;
  };
}

export type CerbosViewRequestData = {
  requestId: string;
  principal?: CerbosPrincipal;
  resource: CerbosResource<'view'>;
  actions: CerbosView[];
  jwtToken?: string;
};

export type CerbosFeatureRequestData = {
  requestId: string;
  principal?: CerbosPrincipal;
  resource: CerbosResource<'feature'>;
  actions: CerbosFeature[];
  jwtToken?: string;
};

export type CerbosRequestData = CerbosViewRequestData | CerbosFeatureRequestData;

export class CerbosApiService {
  async checkAccess(requestData: CerbosRequestData) {
    const cerbosGrpc = new HTTP(resolveCerbosUrl());

    const extractedToken =
      requestData.jwtToken && new TokenService().decodeToken(requestData.jwtToken);

    const response = await cerbosGrpc.checkResource({
      requestId: requestData.requestId,
      principal: {
        id: requestData.principal?.id ?? extractedToken?.userId ?? v4(),
        roles: ['USER'],
        attr: requestData.principal?.attributes as any,
      },
      // Adds our token to the request.
      // Cerbos reads the content, and uses during policy evaluation.
      auxData: isDefined(requestData.jwtToken)
        ? {
            jwt: {
              token: requestData.jwtToken,
            },
          }
        : undefined,
      resource: {
        id: requestData.resource.id ?? v4(),
        kind: requestData.resource?.kind,
        attr: requestData.resource.attributes,
        scope: requestData.resource?.scope,
      },
      actions: requestData.actions,
    });

    return response;
  }
}
